import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth, apiError } from "@/context/AuthContext";
import { toast } from "sonner";
import { Clock3, Building2, User as UserIcon } from "lucide-react";

const DAYS = [["mon", "Lun"], ["tue", "Mar"], ["wed", "Mer"], ["thu", "Gio"], ["fri", "Ven"], ["sat", "Sab"], ["sun", "Dom"]];
const empty = { mon: [], tue: [], wed: [], thu: [], fri: [], sat: [], sun: [] };

function normalize(o) {
  const out = { ...empty };
  for (const [k] of DAYS) { const arr = (o && o[k]) || []; out[k] = [arr[0] || ["", ""], arr[1] || ["", ""]]; }
  return out;
}
function clean(o) {
  const out = {};
  for (const [k] of DAYS) out[k] = (o[k] || []).filter((iv) => iv[0] && iv[1]).map((iv) => [iv[0], iv[1]]);
  return out;
}

export function WorkingHoursCard() {
  const { user, isDirezione, patchUser } = useAuth();
  const [scope, setScope] = useState("me"); // me | azienda
  const [sched, setSched] = useState(normalize(empty));
  const [def, setDef] = useState(empty);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings/orario-lavorativo").then((r) => setDef(r.data.orario || {})).catch(() => {});
  }, []);
  useEffect(() => {
    if (scope === "me") setSched(normalize(user?.orario_lavorativo || def));
    else setSched(normalize(def));
  }, [scope, def]); // eslint-disable-line

  const setIv = (day, idx, pos, val) => setSched((s) => {
    const arr = s[day].map((iv) => [...iv]); arr[idx][pos] = val; return { ...s, [day]: arr };
  });

  const save = async () => {
    setSaving(true);
    const payload = clean(sched);
    try {
      if (scope === "azienda") {
        await api.put("/settings/orario-lavorativo", { orario: payload });
        setDef(payload); toast.success("Orario aziendale predefinito aggiornato.");
      } else {
        await api.put("/profile/preferences", { orario_lavorativo: payload });
        patchUser({ orario_lavorativo: payload }); toast.success("Il tuo orario lavorativo è stato salvato.");
      }
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const resetMine = async () => {
    setSaving(true);
    try { await api.put("/profile/preferences", { orario_lavorativo: {} }); patchUser({ orario_lavorativo: null }); setSched(normalize(def)); toast.success("Ripristinato l'orario aziendale."); }
    catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <div className="card-immo p-5" data-testid="working-hours-card">
      <div className="flex items-center gap-2.5 mb-3">
        <div className="w-9 h-9 rounded-xl bg-[#FC82ED]/15 border border-[#FC82ED]/30 flex items-center justify-center text-[#FC82ED]"><Clock3 size={18} /></div>
        <h3 className="font-display font-bold text-base">Orario lavorativo</h3>
      </div>
      {isDirezione && (
        <div className="flex gap-1 bg-[#F7F7F8] border border-[var(--border)] rounded-xl p-1 mb-3 w-fit">
          <button data-testid="wh-scope-me" onClick={() => setScope("me")} className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 ${scope === "me" ? "bg-[#FC82ED] text-black" : "text-slate-600"}`}><UserIcon size={13} /> Il mio</button>
          <button data-testid="wh-scope-azienda" onClick={() => setScope("azienda")} className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 ${scope === "azienda" ? "bg-[#FC82ED] text-black" : "text-slate-600"}`}><Building2 size={13} /> Predefinito aziendale</button>
        </div>
      )}
      <p className="text-sm text-[var(--muted)] mb-3">Usato per evidenziare gli slot liberi e proporre il primo slot disponibile nella vista Disponibilità.{scope === "me" && !isDirezione ? "" : ""}</p>
      <div className="space-y-2">
        {DAYS.map(([k, l]) => (
          <div key={k} className="flex items-center gap-2 flex-wrap" data-testid={`wh-day-${k}`}>
            <span className="w-10 text-xs font-semibold text-slate-600">{l}</span>
            {[0, 1].map((idx) => (
              <span key={idx} className="flex items-center gap-1">
                <input type="time" data-testid={`wh-${k}-${idx}-start`} value={sched[k][idx][0]} onChange={(e) => setIv(k, idx, 0, e.target.value)}
                       className="px-2 py-1 rounded-md border border-[var(--border)] text-xs" />
                <span className="text-[var(--muted)] text-xs">–</span>
                <input type="time" data-testid={`wh-${k}-${idx}-end`} value={sched[k][idx][1]} onChange={(e) => setIv(k, idx, 1, e.target.value)}
                       className="px-2 py-1 rounded-md border border-[var(--border)] text-xs" />
              </span>
            ))}
          </div>
        ))}
      </div>
      <div className="flex gap-2 mt-4">
        <button data-testid="wh-save" onClick={save} disabled={saving} className="flex-1 py-2.5 rounded-lg bg-[#FC82ED] hover:bg-[#e06dd2] text-black font-semibold text-sm disabled:opacity-60">Salva orario</button>
        {scope === "me" && <button data-testid="wh-reset" onClick={resetMine} disabled={saving} className="px-4 py-2.5 rounded-lg bg-[#F7F7F8] border border-[var(--border)] text-sm font-medium hover:bg-slate-100">Usa aziendale</button>}
      </div>
    </div>
  );
}
